import type { ReactNode } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { useDeleteRecord, useRestoreRecord, useUpdateRecord } from '../../../data/hooks';
import {
  ContextMenu,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  useToast,
} from '../../../ui';
import { resolveBoardGroupField } from '../views/viewState';
import { useOpenRecord } from './openRecord';

/**
 * THE ROW'S CONTEXT MENU (spec/screens/list.md section 2 item 8, section 7 "the actions are
 * toolbar items and the context menu").
 *
 * WHAT IS IN IT, and the two places it departs from the spec's draft list, both recorded in
 * `DEVIATIONS-UI-FOUNDATION.md`:
 *
 *  - THERE IS NO SEPARATE "DELETE". In this platform a record delete IS the archive
 *    (`useDeleteRecord` soft-deletes, `admin-area.md` D21, and the row comes back with
 *    Restore), so a menu offering Archive AND Delete would offer one operation twice under
 *    two names, and the red one would promise something the server does not do. Archive is
 *    the destructive item: red, last, behind a separator, exactly where the HIG puts it.
 *  - "ASSIGN TO" IS ABSENT RATHER THAN DIMMED. The rulebook's own S9 says a context menu
 *    HIDES what does not apply, and there is no owner concept on an object yet - an
 *    assignee would have to be invented per object here, which is a product decision and
 *    not a menu's.
 *
 * WHAT IT ACTS ON: the selection, when the row belongs to it, and the row alone otherwise
 * (`RowSelectionProvider.contextClick`). That is the macOS rule and it is what makes a
 * secondary click on one of three selected rows archive three.
 */
export interface RecordRowMenuProps {
  readonly object: ObjectMetadata;
  /** The fields this VIEW shows. The stage submenu is resolved over them - see below. */
  readonly fields: readonly FieldMetadata[];
  /** The rows the menu acts on, already resolved by the caller. Never empty. */
  readonly targetIds: readonly string[];
  /** The Archived segment: every target is a tombstone, so the exit is Restore. */
  readonly archived: boolean;
  /**
   * The field the view groups its board by, which is what "stage" means on this object
   * (`resolveBoardGroupField`). Passed down rather than resolved from scratch so the menu,
   * the detail pane and the board cannot pick three different SELECT fields.
   */
  readonly boardGroupFieldId: string | null;
  /** Told before the menu opens, so the row it opened on joins the selection first. */
  readonly onOpen: () => void;
  readonly children: ReactNode;
}

export function RecordRowMenu({
  object,
  fields,
  targetIds,
  archived,
  boardGroupFieldId,
  onOpen,
  children,
}: RecordRowMenuProps) {
  /**
   * ONE OPEN PATH FOR THE SCREEN (wave 3, L-B1). This item used to call `navigate` directly
   * while the row's title was a `TransitionLink`, so the same destination was reached two
   * ways and only one of them animated. `useOpenRecord` is what the double click, Enter and
   * the pane's Open take too.
   */
  const open = useOpenRecord(object.id);
  const toast = useToast();
  const update = useUpdateRecord(object.id);
  const remove = useDeleteRecord(object.id);
  const restore = useRestoreRecord(object.id);

  const single = targetIds.length === 1 ? targetIds[0] : undefined;
  const noun = targetIds.length === 1 ? object.labelSingular : object.labelPlural;
  const subject = `${targetIds.length} ${noun.toLowerCase()}`;

  /**
   * THE STAGE FIELD IS THE ONE THE VIEW SHOWS (round 2, finding F2): the board field when the
   * view keeps it, otherwise the first SELECT column in the view, resolved by the function the
   * board and the saved view already use so "stage" means one field on every surface. It was
   * resolved over EVERY field of the object and read "Set Language" on a view showing Status.
   * A view with no select column has no stage, and the submenu is then absent rather than
   * empty - S9 hides what does not apply instead of dimming it.
   */
  const stageFieldId = resolveBoardGroupField(boardGroupFieldId, fields);
  const stage = fields.find((field) => field.id === stageFieldId);
  const stageOptions = stage?.options?.selectOptions ?? [];

  const setStage = (optionId: string, label: string) => {
    for (const recordId of targetIds) {
      update.mutate({ recordId, values: { [stage?.id ?? '']: optionId } });
    }
    toast({ title: `${subject} moved to ${label}`, tone: 'neutral' });
  };

  const runArchive = () => {
    for (const recordId of targetIds) remove.mutate(recordId);
    toast({ title: `${subject} archived`, tone: 'neutral' });
  };

  const runRestore = () => {
    for (const recordId of targetIds) restore.mutate(recordId);
    toast({ title: `${subject} restored`, tone: 'success' });
  };

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (open) onOpen();
      }}
      trigger={children}
    >
      {single !== undefined && (
        <>
          <ContextMenuItem icon="external" onSelect={() => open(single)}>
            Open
          </ContextMenuItem>
          <ContextMenuItem
            icon="link"
            onSelect={() =>
              window.open(`/objects/${object.id}/records/${single}`, '_blank', 'noopener')
            }
          >
            Open in new tab
          </ContextMenuItem>
          <ContextMenuSeparator />
        </>
      )}

      {!archived && stage !== undefined && stageOptions.length > 0 && (
        <>
          {/* THE ELLIPSIS IS ABSENT ON PURPOSE (rulebook S10): an ellipsis promises a dialog
              that asks for more, and a submenu already IS the asking. */}
          <ContextMenuSub label={`Set ${stage.label}`} icon="board">
            {stageOptions.map((option) => (
              <ContextMenuItem key={option.id} onSelect={() => setStage(option.id, option.label)}>
                {option.label}
              </ContextMenuItem>
            ))}
          </ContextMenuSub>
          <ContextMenuSeparator />
        </>
      )}

      {archived ? (
        <ContextMenuItem icon="arrow-up" onSelect={runRestore}>
          Restore
        </ContextMenuItem>
      ) : (
        <ContextMenuItem icon="trash" tone="danger" onSelect={runArchive}>
          Archive
        </ContextMenuItem>
      )}
    </ContextMenu>
  );
}
